/**
 * @file 読んだ時間 (学習者 × 資料 × 日の、リーダーで実際に操作していた時間) のクライアント。
 * @what: `GET /families/{fid}/study/plans/{personId}/reading_sessions?from=&to=` (期間の一覧) と、
 *   `POST .../reading_sessions/{sourceId}` (1回ぶんの増分の足し込み)。
 * @why: 学習の積み重ね (学んだ日の暦・計画の本の進み) は「いつ・どの本を・どれだけ読んだか」の履歴から
 *   出す。閲覧位置は最後のページ1値しか持たず、取り組みの記録は手で書くものなので、リーダーが自動で
 *   残す器を別に持つ (代表の判断、2026-09-13)。
 * @failure: ApiError を伝播する。sessions が空 = その期間に何も読んでいない、は正常系である。
 */

import { apiFetch } from "@/shared/api/client";

export interface StudyReadingSession {
  source_id: string;
  /** 端末のローカル日付 (YYYY-MM-DD)。 */
  date: string;
  /** 実際に操作していた秒数の合計である。 */
  active_seconds: number;
  /** この日にリーダーを開いた回数である。 */
  opens: number;
  /** リーダーの連番のページ (文字列のキー) → そのページでの滞在秒である。 */
  pages: Record<string, number>;
  /** この日に開いたいちばん先のページ (連番)。0 = 無し。 */
  max_page: number;
  first_at: string;
  last_at: string;
}

/** 1回ぶんの増分 (前回の送信からに増えたぶん)。 */
export interface StudyReadingSessionDelta {
  date: string;
  active_seconds: number;
  opens: number;
  pages: Record<string, number>;
  max_page: number;
}

function path(familyId: string, personId: string): string {
  return `/families/${encodeURIComponent(familyId)}/study/plans/${encodeURIComponent(
    personId,
  )}/reading_sessions`;
}

export async function fetchReadingSessions(
  familyId: string,
  personId: string,
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<StudyReadingSession[]> {
  const query = `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
  const res = await apiFetch<{ sessions: StudyReadingSession[] }>(
    `${path(familyId, personId)}${query}`,
    signal ? { signal } : {},
  );
  return res.sessions;
}

export async function postReadingSession(
  familyId: string,
  personId: string,
  sourceId: string,
  delta: StudyReadingSessionDelta,
  opts?: { keepalive?: boolean },
): Promise<StudyReadingSession> {
  return apiFetch<StudyReadingSession>(
    `${path(familyId, personId)}/${encodeURIComponent(sourceId)}`,
    { method: "POST", body: delta, ...(opts?.keepalive ? { keepalive: true } : {}) },
  );
}
